import React, { memo } from 'react';
import { Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useSearchableSelect, SearchableSelectItem } from '../../hooks/useSearchableSelect';
import { theme } from '../../theme';

interface SearchableInputProps<T extends SearchableSelectItem> {
  /** Items a buscar */
  items: T[];
  /** Valor seleccionado */
  value?: T | null;
  /** Callback cuando se selecciona un item */
  onSelect?: (item: T | null) => void;
  /** Placeholder del input */
  placeholder?: string;
  /** Tamaño del input */
  size?: 'small' | 'middle' | 'large';
  /** Si el input está deshabilitado */
  disabled?: boolean;
  /** Clase CSS del contenedor */
  containerClassName?: string;
  /** Campos adicionales para buscar */
  searchFields?: string[];
  /** Estilos personalizados */
  style?: React.CSSProperties;
}

/**
 * Componente Input con búsqueda y dropdown reutilizable
 * Usa useSearchableSelect internamente
 * 
 * Optimizado con React.memo para evitar re-renders innecesarios
 * 
 * @example
 * <SearchableInput
 *   items={clientes}
 *   value={selectedCliente}
 *   onSelect={setSelectedCliente}
 *   placeholder="Buscar cliente..."
 * />
 */
export const SearchableInput = memo(<T extends SearchableSelectItem>({
  items,
  value,
  onSelect,
  placeholder = 'Buscar...',
  size = 'large',
  disabled = false,
  containerClassName = 'searchable-select-container',
  searchFields = [],
  style = {},
}: SearchableInputProps<T>) => {
  const {
    searchText,
    selectedItem,
    showDropdown,
    filteredItems,
    handleSelect,
    handleClear,
    handleSearchChange,
    clearSelection,
    setShowDropdown,
  } = useSearchableSelect(items, { containerSelector: `.${containerClassName}`, searchFields });

  // Sincronizar cuando el padre cambia el valor externamente
  React.useEffect(() => {
    if (value !== selectedItem) {
      if (value) {
        handleSelect(value);
      } else {
        clearSelection();
      }
    }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const displayValue = selectedItem
    ? selectedItem.nc
      ? `${selectedItem.nc} - ${selectedItem.denominacion}`
      : selectedItem.denominacion
    : searchText;

  return (
    <div className={containerClassName} style={{ position: 'relative', width: '100%', ...style }}>
      <Input
        placeholder={placeholder}
        size={size}
        prefix={<SearchOutlined />}
        value={displayValue}
        onChange={(e) => handleSearchChange(e.target.value)}
        onClick={() => { if (!disabled) setShowDropdown(true); }}
        allowClear={!!selectedItem}
        onClear={() => { handleClear(); onSelect?.(null); }}
        disabled={disabled}
        style={{
          backgroundColor: selectedItem ? theme.colors.backgroundGray : 'white',
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
      />
      
      {/* Dropdown de resultados */}
      {showDropdown && filteredItems.length > 0 && !selectedItem && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            backgroundColor: 'white',
            border: '1px solid #ddd',
            borderRadius: '4px',
            maxHeight: '200px',
            overflowY: 'auto',
            zIndex: 1000,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            marginTop: '4px',
          }}
        >
          {filteredItems.map((item) => (
            <div
              key={item.id}
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                borderBottom: '1px solid #eee',
                textAlign: 'left',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = '#f5f5f5';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = 'white';
              }}
              onClick={() => { handleSelect(item); onSelect?.(item); }}
            >
              {item.nc ? `${item.nc} - ${item.denominacion}` : item.denominacion}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}) as <T extends SearchableSelectItem>(props: SearchableInputProps<T>) => React.ReactElement;

// Asignar displayName después del type assertion
if (typeof SearchableInput === 'function') {
  (SearchableInput as any).displayName = 'SearchableInput';
}

